import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, Users, DollarSign, Sparkles, Megaphone, Star, TrendingUp, UserPlus, Coins, ShieldCheck, CalendarDays, Loader2, Save, Wand2, Send, Mail, KeyRound, Banknote, Rocket, X, Bitcoin, CheckCircle2, XCircle, AlertCircle, Flag, Heart } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MonthlyRefreshCard } from "@/components/MonthlyRefreshCard";
import { HelpInbox } from "@/components/HelpInbox";

const STAT_META = [
  { key: "total_users", label: "Total members", icon: Users },
  { key: "new_users_7d", label: "New this week", icon: UserPlus },
  { key: "total_creators", label: "Creators", icon: Crown },
  { key: "active_subscriptions", label: "Active subs", icon: Star },
  { key: "total_revenue", label: "Revenue", icon: DollarSign, money: true },
  { key: "coins_in_circulation", label: "Empire Coins", icon: Coins },
  { key: "verified_users", label: "Verified", icon: ShieldCheck },
  { key: "map_public", label: "On the map", icon: Users },
  { key: "waitlist_count", label: "Waitlist", icon: Mail },
];

const ACTION_ICON = { event: CalendarDays, announcement: Megaphone, spotlight: Star, content: Sparkles };

export default function AdminCommand() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [cfg, setCfg] = useState({ hero_headline: "", hero_subtext: "", announcement: "", announcement_active: false });
  const [boostHours, setBoostHours] = useState(48);
  const [savingCfg, setSavingCfg] = useState(false);
  const [creators, setCreators] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [blast, setBlast] = useState({ subject: "", message: "" });
  const [blasting, setBlasting] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [founding, setFounding] = useState([]);
  const [foundingEmail, setFoundingEmail] = useState("");
  const [grantingFounding, setGrantingFounding] = useState(false);
  const [invite, setInvite] = useState({ emails: "", subject: "", message: "" });
  const [inviting, setInviting] = useState(false);
  const [cryptoHealth, setCryptoHealth] = useState(null);
  const [checkingCrypto, setCheckingCrypto] = useState(false);
  const [cryptoReview, setCryptoReview] = useState([]);
  const [reports, setReports] = useState([]);
  const [sessionReqs, setSessionReqs] = useState([]);
  const reviewReport = async (id, action) => {
    try {
      await api.post("/admin/reports/review", { report_id: id, action });
      setReports((p) => p.filter((x) => x.id !== id));
      toast.success(action === "dismiss" ? "Report dismissed" : "Marked as actioned");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const [waitlist, setWaitlist] = useState(null);
  const [exportingWl, setExportingWl] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoTotal, setDemoTotal] = useState(null);
  const seedDemo = async () => {
    setDemoBusy(true);
    try { const { data } = await api.post("/admin/seed-demo-profiles"); setDemoTotal(data.total_demo);
      toast.success(`Added ${data.created} — ${data.total_demo} demo profiles live in Discover`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setDemoBusy(false); }
  };
  const removeDemo = async () => {
    setDemoBusy(true);
    try { const { data } = await api.delete("/admin/demo-profiles"); setDemoTotal(0);
      toast.success(`Removed ${data.deleted} demo profiles`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setDemoBusy(false); }
  };
  useEffect(() => { api.get("/admin/crypto-underpaid").then((r) => setCryptoReview(r.data)).catch(() => {}); }, []);
  const exportWaitlist = async () => {
    setExportingWl(true);
    try {
      const res = await api.get("/admin/waitlist/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Waitlist exported");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Export failed"); }
    finally { setExportingWl(false); }
  };
  const resolveUnderpaid = async (row, action) => {
    try {
      await api.post(`/admin/crypto-underpaid/${row.id}/resolve?action=${action}`);
      setCryptoReview((cur) => cur.filter((x) => x.id !== row.id));
      toast.success(action === "credit" ? "Coins credited — customer made whole 👑" : "Marked as refunded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const runCryptoCheck = async () => {
    setCheckingCrypto(true);
    try { const { data } = await api.get("/admin/crypto-health"); setCryptoHealth(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCheckingCrypto(false); }
  };

  useEffect(() => {
    api.get("/admin/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/site-config").then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
    api.get("/admin/creators-manage").then((r) => setCreators(r.data)).catch(() => {});
    api.get("/admin/withdrawals?status=pending").then((r) => setPayouts(r.data)).catch(() => {});
    api.get("/admin/founding-creators").then((r) => setFounding(r.data)).catch(() => {});
    api.get("/admin/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
    api.get("/admin/reports?status=open").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/session-requests").then((r) => setSessionReqs(r.data)).catch(() => {});
  }, []);

  const grantFounding = async () => {
    if (!foundingEmail.trim()) { toast.error("Enter the creator's email"); return; }
    setGrantingFounding(true);
    try {
      const { data } = await api.post("/admin/founding-creator", { email: foundingEmail.trim(), founding: true });
      setFounding((cur) => cur.some((f) => f.id === data.user.id) ? cur : [{ ...data.user, membership_active: false }, ...cur]);
      setFoundingEmail("");
      toast.success(`${data.user.name || data.user.email} is now a Founding Creator 👑`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGrantingFounding(false); }
  };

  const removeFounding = async (f) => {
    try {
      await api.post("/admin/founding-creator", { user_id: f.id, founding: false });
      setFounding((cur) => cur.filter((x) => x.id !== f.id));
      toast.success(`${f.name || f.email} removed from Founding Creators`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendInvites = async () => {
    const emails = invite.emails.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    if (!emails.length) { toast.error("Add at least one valid email"); return; }
    setInviting(true);
    try {
      const { data } = await api.post("/admin/invite-creators", { emails, subject: invite.subject, message: invite.message });
      toast.success(`Invite sent to ${data.count} creator${data.count === 1 ? "" : "s"} 👑`);
      setInvite({ emails: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setInviting(false); }
  };

  const reviewPayout = async (w, approve) => {
    let note = "";
    if (!approve) { note = window.prompt("Reason for rejecting this payout (optional):") || ""; }
    try {
      await api.post("/admin/withdrawals/review", { withdrawal_id: w.id, approve, note });
      setPayouts((cur) => cur.filter((x) => x.id !== w.id));
      toast.success(approve ? `Marked $${w.amount_usd.toFixed(2)} as paid 💸` : "Payout rejected");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };


  const saveCfg = async () => {
    setSavingCfg(true);
    try { await api.put("/admin/site-config", cfg); toast.success("Site updated — members will see it live"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCfg(false); }
  };

  const toggleFeature = async (c) => {
    try {
      const { data } = await api.post("/admin/feature-creator", { creator_id: c.id, featured: !c.featured });
      setCreators((cur) => cur.map((x) => (x.id === c.id ? { ...x, featured: data.featured } : x)));
      toast.success(data.featured ? `${c.name} spotlighted ⭐` : `${c.name} un-featured`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const genIdeas = async () => {
    setIdeasLoading(true);
    try { const { data } = await api.post("/admin/growth-ideas", { focus: "" }); setIdeas(data.ideas); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setIdeasLoading(false); }
  };

  const sendBlast = async () => {
    if (!blast.subject.trim() || !blast.message.trim()) { toast.error("Add a subject and message first"); return; }
    setBlasting(true);
    try {
      const { data } = await api.post("/admin/broadcast", blast);
      toast.success(`Email sent to ${data.count} member${data.count === 1 ? "" : "s"} 📣`);
      setBlast({ subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBlasting(false); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated 🔐");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setChangingPw(false); }
  };


  const applyIdea = (idea) => {    if (idea.action === "announcement") {
      setCfg((c) => ({ ...c, announcement: idea.title, announcement_active: true }));
      toast.success("Loaded into announcement — review & save below");
      document.getElementById("cc-content")?.scrollIntoView({ behavior: "smooth" });
    } else if (idea.action === "content") {
      document.getElementById("cc-content")?.scrollIntoView({ behavior: "smooth" });
    } else if (idea.action === "spotlight") {
      document.getElementById("cc-spotlight")?.scrollIntoView({ behavior: "smooth" });
    } else if (idea.action === "event") {
      navigate("/events");
    }
  };

  if (user && user.role !== "admin") return <div className="p-12 text-center text-[#A0A0A0]">Admins only.</div>;

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto">
      <div className="overline mb-1">Owner</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-8 flex items-center gap-3"><Crown className="text-[#D4AF37]" /> Empire Command Center</h2>

      {/* Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {STAT_META.map(({ key, label, icon: Icon, money }, i) => (
          <motion.div key={key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            data-testid={`stat-${key}`} className="pe-card rounded-2xl p-5">
            <Icon className="text-[#D4AF37] mb-2" size={20} />
            <div className="font-heading text-2xl gold-text">{ov ? (money ? `$${ov[key]}` : ov[key]) : "…"}</div>
            <div className="text-xs text-[#A0A0A0]">{label}</div>
          </motion.div>
        ))}
      </div>

      {/* Content control */}
      <div id="cc-content" className="pe-card rounded-2xl p-6 mb-8">
        <div className="font-heading text-2xl mb-4 flex items-center gap-2"><Sparkles size={20} className="text-[#D4AF37]" /> Look & Content</div>
        <div className="space-y-4">
          <div>
            <div className="text-xs text-[#A0A0A0] mb-1">Landing headline (leave blank for default)</div>
            <input data-testid="cfg-headline" value={cfg.hero_headline} onChange={(e) => setCfg({ ...cfg, hero_headline: e.target.value })}
              placeholder="Where Desire Meets Discretion" className="pe-input w-full rounded-lg px-4 py-3" />
          </div>
          <div>
            <div className="text-xs text-[#A0A0A0] mb-1">Landing sub-text</div>
            <input data-testid="cfg-subtext" value={cfg.hero_subtext} onChange={(e) => setCfg({ ...cfg, hero_subtext: e.target.value })}
              placeholder="An exclusive members-only playground for bold adults." className="pe-input w-full rounded-lg px-4 py-3" />
          </div>
          <div>
            <div className="text-xs text-[#A0A0A0] mb-1">Site-wide announcement banner</div>
            <input data-testid="cfg-announcement" value={cfg.announcement} onChange={(e) => setCfg({ ...cfg, announcement: e.target.value })}
              placeholder="🔥 Kinky Monday live tonight at 9pm!" className="pe-input w-full rounded-lg px-4 py-3" />
          </div>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input data-testid="cfg-announcement-active" type="checkbox" checked={cfg.announcement_active}
              onChange={(e) => setCfg({ ...cfg, announcement_active: e.target.checked })} />
            Show the announcement banner to all members
          </label>
          <div className="border-t border-[#2A2A2A] pt-3 mt-1">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input data-testid="cfg-crypto-boost" type="checkbox" checked={!!cfg.crypto_bonus_boost}
                onChange={(e) => setCfg({ ...cfg, crypto_bonus_boost: e.target.checked, crypto_bonus_boost_pct: cfg.crypto_bonus_boost_pct || 0.2, crypto_bonus_boost_ends_at: e.target.checked ? new Date(Date.now() + (boostHours || 48) * 3600000).toISOString() : null })} />
              🎁 Double-Bonus Weekend — boost the crypto bonus temporarily
            </label>
            {cfg.crypto_bonus_boost && (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-[#A0A0A0]">
                <span>Boosted bonus:</span>
                <input data-testid="cfg-crypto-boost-pct" type="number" min="10" max="100" step="5"
                  value={Math.round((cfg.crypto_bonus_boost_pct || 0.2) * 100)}
                  onChange={(e) => setCfg({ ...cfg, crypto_bonus_boost_pct: Math.max(0, Number(e.target.value) / 100) })}
                  className="pe-input w-20 rounded-lg px-3 py-1.5" />
                <span>%</span>
                <span className="ml-1">Ends in</span>
                <input data-testid="cfg-crypto-boost-hours" type="number" min="1" max="336" step="1"
                  value={boostHours}
                  onChange={(e) => { const h = Math.max(1, Number(e.target.value)); setBoostHours(h); setCfg({ ...cfg, crypto_bonus_boost_ends_at: new Date(Date.now() + h * 3600000).toISOString() }); }}
                  className="pe-input w-20 rounded-lg px-3 py-1.5" />
                <span>hours</span>
              </div>
            )}
          </div>
          <button data-testid="save-cfg" onClick={saveCfg} disabled={savingCfg}
            className="btn-gold rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-60"><Save size={15} /> {savingCfg ? "Saving…" : "Publish changes"}</button>
        </div>
      </div>

      {/* Crypto payments to review (underpaid) */}
      {cryptoReview.length > 0 && (
        <div className="pe-card rounded-2xl p-6 mb-8 border border-yellow-500/30" data-testid="crypto-review-card">
          <div className="font-heading text-2xl mb-1 flex items-center gap-2"><AlertCircle size={20} className="text-yellow-400" /> Crypto payments to review</div>
          <div className="text-xs text-[#A0A0A0] mb-4">These customers paid noticeably less than expected (beyond normal fees). Credit their coins to make them whole, or mark as refunded once you've refunded them.</div>
          <div className="space-y-3">
            {cryptoReview.map((r) => (
              <div key={r.id} data-testid={`crypto-review-${r.id}`} className="rounded-lg border border-[#2A2A2A] p-3">
                <div className="text-sm text-white">{r.name || r.email || "Customer"} <span className="text-[#A0A0A0]">· ${Number(r.amount_usd || 0).toFixed(2)} order · {r.coins} coins</span></div>
                <div className="text-xs text-[#A0A0A0] mt-0.5">Expected {r.expected} USDT · received {r.received} USDT (short {Number(r.shortfall || 0).toFixed(2)})</div>
                <div className="flex gap-2 mt-2">
                  <button data-testid={`crypto-credit-${r.id}`} onClick={() => resolveUnderpaid(r, "credit")} className="btn-gold rounded-full px-4 py-1.5 text-xs">Credit their coins</button>
                  <button data-testid={`crypto-refunded-${r.id}`} onClick={() => resolveUnderpaid(r, "refunded")} className="btn-outline-gold rounded-full px-4 py-1.5 text-xs">Mark refunded</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Crypto Payment Health */}
      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="session-requests-card">
        <h3 className="font-heading text-2xl flex items-center gap-2 mb-1"><Heart className="text-[#E63946]" size={20} /> 1-on-1 session requests {sessionReqs.length > 0 && <span className="text-sm text-[#E63946]">· {sessionReqs.length}</span>}</h3>
        <p className="text-sm text-[#A0A0A0] mb-4">Members who've requested a private 1-on-1 from your themed nights.</p>
        {sessionReqs.length === 0 ? (
          <div className="text-sm text-[#A0A0A0]">No open requests yet.</div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {sessionReqs.map((s) => (
              <div key={s.id} data-testid={`session-req-${s.id}`} className="flex items-center justify-between gap-3 py-2 border-b border-[#2A2A2A] text-sm">
                <div className="min-w-0">
                  <span className="text-white">{s.user_name || "Member"}</span>
                  <span className="text-[#A0A0A0]"> · {s.event_title}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] px-2 py-0.5 rounded-full border border-[#E63946]/40 text-[#E63946]">{s.session_type}</span>
                  <button data-testid={`session-view-${s.id}`} onClick={() => navigate(`/creator/${s.user_id}`)}
                    className="btn-outline-gold px-3 py-1 rounded-full text-xs">View</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="reports-card">
        <h3 className="font-heading text-2xl flex items-center gap-2 mb-1"><Flag className="text-[#E63946]" size={20} /> Open reports {reports.length > 0 && <span className="text-sm text-[#E63946]">· {reports.length}</span>}</h3>
        <p className="text-sm text-[#A0A0A0] mb-4">Member reports of users and content. Review and take action.</p>
        {reports.length === 0 ? (
          <div className="text-sm text-[#A0A0A0]">No open reports. 🎉</div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {reports.map((r) => (
              <div key={r.id} data-testid={`report-${r.id}`} className="rounded-xl border border-[#2A2A2A] p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm text-white"><span className="text-[#E63946] font-medium">{r.reason}</span> · <span className="text-[#A0A0A0]">{r.target_type}</span></div>
                    {r.details && <div className="text-xs text-[#A0A0A0] mt-1">"{r.details}"</div>}
                    <div className="text-[11px] text-[#8A8A8A] mt-1.5">
                      Reported by {r.reporter?.name || r.reporter?.email || "member"}
                      {r.target_user && <> · about {r.target_user.name || r.target_user.email}</>}
                      {r.created_at && <> · {new Date(r.created_at).toLocaleString()}</>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {r.target_type === "user" && (
                      <button data-testid={`report-view-${r.id}`} onClick={() => navigate(`/creator/${r.target_id}`)}
                        className="btn-outline-gold px-3 py-1.5 rounded-full text-xs">View</button>
                    )}
                    <button data-testid={`report-action-${r.id}`} onClick={() => reviewReport(r.id, "actioned")}
                      className="px-3 py-1.5 rounded-full text-xs bg-[#E63946] text-white">Actioned</button>
                    <button data-testid={`report-dismiss-${r.id}`} onClick={() => reviewReport(r.id, "dismiss")}
                      className="px-3 py-1.5 rounded-full text-xs border border-[#2A2A2A] text-[#A0A0A0] hover:text-white">Dismiss</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="demo-profiles-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="font-heading text-2xl flex items-center gap-2"><Users className="text-[#D4AF37]" size={20} /> Demo profiles</h3>
          <div className="flex items-center gap-2">
            <button data-testid="seed-demo" onClick={seedDemo} disabled={demoBusy}
              className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-50">
              {demoBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Populate Discover
            </button>
            <button data-testid="remove-demo" onClick={removeDemo} disabled={demoBusy}
              className="btn-outline-gold px-4 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-50">
              <X size={14} /> Remove all
            </button>
          </div>
        </div>
        <p className="text-sm text-[#A0A0A0]">Adds 10 richly-styled sample members so the browse page feels alive before real sign-ups. Safe to remove anytime once you have real members.{demoTotal !== null && <span className="text-[#D4AF37]"> · {demoTotal} live now</span>}</p>
      </div>

      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="custodian-card">
        <h3 className="font-heading text-2xl flex items-center gap-2 mb-1"><ShieldCheck className="text-[#D4AF37]" size={20} /> Custodian of Records (2257)</h3>
        <p className="text-sm text-[#A0A0A0] mb-4">Shown publicly on your Legal → 18 U.S.C. 2257 page and stamped onto the vault CSV export. Use a registered-agent / business address, never your home address.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#A0A0A0]">Custodian name</label>
            <input data-testid="custodian-name" value={cfg.custodian_name || ""} onChange={(e) => setCfg({ ...cfg, custodian_name: e.target.value })}
              placeholder="e.g. Jane Doe, Custodian of Records" className="pe-input w-full rounded-lg px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0]">Records inquiries email</label>
            <input data-testid="custodian-email" value={cfg.custodian_email || ""} onChange={(e) => setCfg({ ...cfg, custodian_email: e.target.value })}
              placeholder="records@yourdomain.com" className="pe-input w-full rounded-lg px-3 py-2 mt-1 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-[#A0A0A0]">Records address (public)</label>
            <textarea data-testid="custodian-address" value={cfg.custodian_address || ""} onChange={(e) => setCfg({ ...cfg, custodian_address: e.target.value })}
              rows={2} placeholder="123 Registered Agent St, Suite 100, City, State ZIP, Country" className="pe-input w-full rounded-lg px-3 py-2 mt-1 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-[#A0A0A0]">Public support / safety email</label>
            <input data-testid="support-email" value={cfg.support_email || ""} onChange={(e) => setCfg({ ...cfg, support_email: e.target.value })}
              placeholder="support@yourdomain.com" className="pe-input w-full rounded-lg px-3 py-2 mt-1 text-sm" />
          </div>
        </div>
        <button data-testid="save-custodian" onClick={saveCfg} disabled={savingCfg}
          className="btn-gold rounded-full px-6 py-2 text-sm flex items-center gap-2 mt-4 disabled:opacity-60"><Save size={15} /> {savingCfg ? "Saving…" : "Save legal details"}</button>
      </div>

      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="waitlist-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-heading text-2xl flex items-center gap-2"><Mail className="text-[#D4AF37]" size={20} /> Early-access waitlist</h3>
          <div className="flex items-center gap-3">
            <span data-testid="waitlist-total" className="text-sm text-[#A0A0A0]"><span className="gold-text font-heading text-lg">{waitlist?.total ?? 0}</span> signups</span>
            <button data-testid="waitlist-export" onClick={exportWaitlist} disabled={exportingWl || !waitlist?.total}
              className="btn-outline-gold px-4 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-50">
              {exportingWl ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Export CSV
            </button>
          </div>
        </div>
        {waitlist?.entries?.length ? (
          <div className="max-h-56 overflow-y-auto divide-y divide-[#2A2A2A]">
            {waitlist.entries.map((e, i) => (
              <div key={i} data-testid={`waitlist-row-${i}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="text-white truncate">{e.email}</span>
                <span className="text-xs text-[#A0A0A0] flex-shrink-0">
                  {e.converted && <span className="text-[#22c55e] mr-2">✓ joined</span>}
                  {e.created_at ? new Date(e.created_at).toLocaleDateString() : ""}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-[#A0A0A0] py-4">No signups yet — share your landing page to start capturing early-access emails.</div>
        )}
      </div>

      <div className="pe-card rounded-2xl p-6 mb-8 flex items-center gap-4" data-testid="vault-link-card">
        <ShieldCheck className="text-[#D4AF37] flex-shrink-0" size={28} />
        <div className="flex-1">
          <div className="text-white font-medium">ID Records Vault (2257 compliance)</div>
          <div className="text-xs text-[#A0A0A0]">Review pending IDs, search the full archive, and export the age-verification register.</div>
        </div>
        <button data-testid="open-vault" onClick={() => navigate("/admin/verifications")}
          className="btn-gold px-5 py-2 rounded-full text-sm whitespace-nowrap">Open vault</button>
      </div>

      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="crypto-health-card">
        <div className="font-heading text-2xl mb-1 flex items-center gap-2"><Bitcoin size={20} className="text-[#D4AF37]" /> Crypto Payment Health</div>
        <div className="text-xs text-[#A0A0A0] mb-4">Verify your live NOWPayments setup is wired correctly before customers pay with crypto — no money required.</div>
        <button data-testid="run-crypto-check" onClick={runCryptoCheck} disabled={checkingCrypto}
          className="btn-gold rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
          {checkingCrypto ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} {checkingCrypto ? "Checking…" : "Run check"}
        </button>
        {cryptoHealth && (
          <div className="mt-4 space-y-2" data-testid="crypto-health-results">
            {[
              ["API key configured", cryptoHealth.api_key_present],
              ["IPN secret configured", cryptoHealth.ipn_secret_present],
              ["NOWPayments reachable", cryptoHealth.api_reachable],
              ["API key valid", cryptoHealth.api_key_valid],
              ["USDT (TRC-20) available", cryptoHealth.usdt_trc20_available],
            ].map(([label, ok]) => (
              <div key={label} className="flex items-center gap-2 text-sm">
                {ok === true ? <CheckCircle2 size={16} className="text-green-400" /> : ok === false ? <XCircle size={16} className="text-red-400" /> : <AlertCircle size={16} className="text-yellow-400" />}
                <span className={ok === true ? "text-white" : "text-[#A0A0A0]"}>{label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm pt-1">
              <span className="text-[#A0A0A0]">Mode:</span>
              <span className={`px-2 py-0.5 rounded text-xs border ${cryptoHealth.mode === "live" ? "border-green-500/40 text-green-300" : "border-yellow-500/40 text-yellow-300"}`}>{cryptoHealth.mode}</span>
            </div>
            <div className="text-xs text-[#A0A0A0] break-all">IPN callback: <span className="text-white">{cryptoHealth.ipn_callback_url}</span></div>
            <div data-testid="crypto-health-verdict" className={`mt-3 rounded-lg p-3 text-sm font-medium ${cryptoHealth.ready ? "bg-green-500/10 text-green-300 border border-green-500/30" : "bg-yellow-500/10 text-yellow-200 border border-yellow-500/30"}`}>
              {cryptoHealth.ready ? "✓ Crypto is ready to accept payments." : "⚠ Not fully ready — fix the red/amber items above."}
            </div>
            <div className="text-[11px] text-[#6b6b6b] mt-2">Note: NOWPayments doesn't expose your IPN secret, so we can't auto-compare it — make sure the IPN secret matches the one in your NOWPayments dashboard (Store Settings → IPN) and that the callback URL above is added there.</div>
          </div>
        )}
      </div>

      {/* Founding Creators — Free Launch Membership */}
      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="founding-card">
        <div className="font-heading text-2xl mb-1 flex items-center gap-2"><Rocket size={20} className="text-[#D4AF37]" /> Free Launch Membership</div>
        <div className="text-xs text-[#A0A0A0] mb-4">Give hand-picked Founding Creators the $19.99/mo membership free until your full launch. Only creators you mark below get it.</div>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input data-testid="cfg-founding-free" type="checkbox" checked={!!cfg.founding_free_mode}
              onChange={(e) => setCfg({ ...cfg, founding_free_mode: e.target.checked })} />
            🚀 Free-launch mode ON — Founding Creators activate membership for free
          </label>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-[#A0A0A0]">
            <span>Free until (launch date):</span>
            <input data-testid="cfg-founding-date" type="date" value={(cfg.founding_launch_date || "").slice(0, 10)}
              onChange={(e) => setCfg({ ...cfg, founding_launch_date: e.target.value })}
              className="pe-input rounded-lg px-3 py-1.5" />
            <span className="text-[11px]">Blank = 90 days per creator.</span>
          </div>
          <button data-testid="save-founding-cfg" onClick={saveCfg} disabled={savingCfg}
            className="btn-gold rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-60"><Save size={15} /> {savingCfg ? "Saving…" : "Save launch settings"}</button>

          <div className="border-t border-[#2A2A2A] pt-4">
            <div className="text-sm text-white font-medium mb-2">Mark a Founding Creator by email</div>
            <div className="flex flex-wrap gap-2">
              <input data-testid="founding-email" value={foundingEmail} onChange={(e) => setFoundingEmail(e.target.value)}
                placeholder="creator@email.com" className="pe-input flex-1 min-w-[200px] rounded-lg px-4 py-2.5 text-sm" />
              <button data-testid="grant-founding" onClick={grantFounding} disabled={grantingFounding}
                className="btn-gold rounded-full px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
                {grantingFounding ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />} Mark as Founding
              </button>
            </div>
            {founding.length > 0 && (
              <div className="mt-4 space-y-2">
                {founding.map((f) => (
                  <div key={f.id} data-testid={`founding-row-${f.id}`} className="flex items-center gap-3 rounded-lg border border-[#2A2A2A] p-2.5">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-[#1A1A1A] flex-shrink-0">{f.photo && <img src={f.photo} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{f.name || f.email}</div>
                      <div className="text-[11px] text-[#A0A0A0] truncate">{f.email} · {f.membership_active ? "membership active ✓" : "not activated yet"}</div>
                    </div>
                    <button data-testid={`remove-founding-${f.id}`} onClick={() => removeFounding(f)}
                      className="text-[#A0A0A0] hover:text-[#E63946] flex-shrink-0"><X size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite Creators */}
      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="invite-card">
        <div className="font-heading text-2xl mb-1 flex items-center gap-2"><UserPlus size={20} className="text-[#D4AF37]" /> Invite Creators</div>
        <div className="text-xs text-[#A0A0A0] mb-4">Email a branded Founding-Creator invite (keep 100%, free membership, crypto payouts) with a button to your recruitment page. Leave the message blank to use the default pitch.</div>
        <div className="space-y-3">
          <textarea data-testid="invite-emails" value={invite.emails} onChange={(e) => setInvite({ ...invite, emails: e.target.value })}
            rows={2} placeholder="Emails, separated by commas or new lines" className="pe-input w-full rounded-lg px-4 py-3" />
          <input data-testid="invite-subject" value={invite.subject} onChange={(e) => setInvite({ ...invite, subject: e.target.value })}
            placeholder="Subject (optional — default provided)" className="pe-input w-full rounded-lg px-4 py-3" />
          <textarea data-testid="invite-message" value={invite.message} onChange={(e) => setInvite({ ...invite, message: e.target.value })}
            rows={4} placeholder="Custom message (optional — leave blank for the default Founding Creator pitch)" className="pe-input w-full rounded-lg px-4 py-3" />
          <button data-testid="send-invites" onClick={sendInvites} disabled={inviting}
            className="btn-gold rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            {inviting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {inviting ? "Sending…" : "Send invites"}
          </button>
        </div>
      </div>

      <HelpInbox />
      <MonthlyRefreshCard onDone={() => api.get("/admin/creators-manage").then((r) => setCreators(r.data)).catch(() => {})} />

      {/* Spotlight */}
      <div id="cc-spotlight" className="pe-card rounded-2xl p-6 mb-8">
        <div className="font-heading text-2xl mb-1 flex items-center gap-2"><Star size={20} className="text-[#D4AF37]" /> Spotlight Creators</div>
        <div className="text-xs text-[#A0A0A0] mb-4">Featured creators appear first on the Creators page.</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {creators.map((c) => (
            <div key={c.id} data-testid={`manage-creator-${c.id}`} className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] p-3">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-[#1A1A1A] flex-shrink-0">{c.photo && <img src={c.photo} alt="" className="w-full h-full object-cover" />}</div>
              <div className="flex-1 min-w-0"><div className="text-sm text-white truncate">{c.name}</div></div>
              <button data-testid={`feature-${c.id}`} onClick={() => toggleFeature(c)}
                className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1 transition-colors ${c.featured ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>
                <Star size={12} /> {c.featured ? "Featured" : "Feature"}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Creator payout queue */}
      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="admin-payouts-card">
        <div className="font-heading text-2xl mb-1 flex items-center gap-2"><Banknote size={20} className="text-[#D4AF37]" /> Creator Payouts</div>
        <div className="text-xs text-[#A0A0A0] mb-4">Pending withdrawal requests. Send the money via the creator's chosen method, then mark it Paid. Rejecting a coin payout refunds the creator's coins.</div>
        {payouts.length === 0 ? (
          <div className="text-sm text-[#A0A0A0]">No pending payouts 🎉</div>
        ) : (
          <div className="space-y-3">
            {payouts.map((w) => (
              <div key={w.id} data-testid={`payout-request-${w.id}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-[#2A2A2A] pb-3">
                <div className="min-w-0">
                  <div className="text-sm text-white">
                    <span className="gold-text font-semibold">${w.amount_usd.toFixed(2)}</span> — {w.creator.name}
                    <span className="text-[#A0A0A0]"> · {w.kind === "coins" ? `${w.coins} coins` : "cash"}</span>
                  </div>
                  <div className="text-xs text-[#A0A0A0] truncate">{w.method === "paypal" ? "PayPal" : "Bank"}: {w.destination}</div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`payout-approve-${w.id}`} onClick={() => reviewPayout(w, true)}
                    className="btn-gold rounded-full px-4 py-1.5 text-xs">Mark paid</button>
                  <button data-testid={`payout-reject-${w.id}`} onClick={() => reviewPayout(w, false)}
                    className="btn-outline-gold rounded-full px-4 py-1.5 text-xs">Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Re-engagement broadcast */}
      <div className="pe-card rounded-2xl p-6 mb-8">
        <div className="font-heading text-2xl mb-1 flex items-center gap-2"><Mail size={20} className="text-[#D4AF37]" /> Email Blast</div>
        <div className="text-xs text-[#A0A0A0] mb-4">Send a re-engagement email to all verified members (e.g. "Kinky Monday is live tonight!"). Use sparingly.</div>
        <div className="space-y-3">
          <input data-testid="blast-subject" value={blast.subject} onChange={(e) => setBlast({ ...blast, subject: e.target.value })}
            placeholder="Subject — e.g. 🔥 Kinky Monday starts at 9pm" className="pe-input w-full rounded-lg px-4 py-3" />
          <textarea data-testid="blast-message" value={blast.message} onChange={(e) => setBlast({ ...blast, message: e.target.value })}
            rows={4} placeholder="Your message to the members…" className="pe-input w-full rounded-lg px-4 py-3" />
          <button data-testid="send-blast" onClick={sendBlast} disabled={blasting}
            className="btn-gold rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            {blasting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {blasting ? "Sending…" : "Send to all members"}
          </button>
        </div>
      </div>

      {/* Admin security — change password */}
      <div className="pe-card rounded-2xl p-6 mb-8" data-testid="admin-security-card">
        <div className="font-heading text-2xl mb-1 flex items-center gap-2"><KeyRound size={20} className="text-[#D4AF37]" /> Admin Security</div>
        <div className="text-xs text-[#A0A0A0] mb-4">Rotate your admin password. Choose a strong, unique secret — this replaces the seed password.</div>
        <div className="space-y-3 max-w-md">
          <input data-testid="pw-current" type="password" autoComplete="current-password" value={pw.current_password}
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            placeholder="Current password" className="pe-input w-full rounded-lg px-4 py-3" />
          <input data-testid="pw-new" type="password" autoComplete="new-password" value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            placeholder="New password (min 8 characters)" className="pe-input w-full rounded-lg px-4 py-3" />
          <input data-testid="pw-confirm" type="password" autoComplete="new-password" value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            placeholder="Confirm new password" className="pe-input w-full rounded-lg px-4 py-3" />
          <button data-testid="change-password-btn" onClick={changePassword} disabled={changingPw}
            className="btn-gold rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            {changingPw ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} {changingPw ? "Updating…" : "Update password"}
          </button>
        </div>
      </div>

      {/* Growth ideas */}
      <div className="pe-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-2xl flex items-center gap-2"><TrendingUp size={20} className="text-[#D4AF37]" /> Growth Ideas</div>
          <button data-testid="gen-ideas" onClick={genIdeas} disabled={ideasLoading}
            className="btn-outline-gold rounded-full px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            {ideasLoading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} {ideasLoading ? "Thinking…" : "Get ideas"}
          </button>
        </div>
        {ideas.length === 0 ? (
          <div className="text-sm text-[#A0A0A0]">Tap "Get ideas" for smart, one-click growth moves tailored to your Empire.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {ideas.map((idea, i) => {
              const Icon = ACTION_ICON[idea.action] || Sparkles;
              return (
                <div key={i} data-testid={`idea-${i}`} className="rounded-xl border border-[#2A2A2A] p-4">
                  <div className="flex items-center gap-2 text-white font-medium mb-1"><Icon size={15} className="text-[#D4AF37]" /> {idea.title}</div>
                  <div className="text-xs text-[#A0A0A0] mb-3">{idea.description}</div>
                  <button data-testid={`apply-idea-${i}`} onClick={() => applyIdea(idea)} className="btn-outline-gold rounded-full px-4 py-1.5 text-xs">Apply</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
